import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  computed,
  signal,
  output,
  OnInit,
  effect,
} from '@angular/core';
import { TranslocoService, TranslocoDirective } from '@ngneat/transloco';

import {
  OptionConfiguration,
  ProductOptionItem,
  ProductOptionRangeType,
  CartItemOptionItems,
  SUPPORTED_LANGUAGES,
  StockDisplayCondition,
} from '@pedix-workspace/utils';

import { PropInSelectedLanguagePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import {
  FormValidationComponent,
  InputCheckboxGroupComponent,
  InputRadioGroupComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { ProductOptionItemRadioComponent } from './product-option-item-radio/product-option-item-radio.component';
import { ProductOptionItemCheckboxComponent } from './product-option-item-checkbox/product-option-item-checkbox.component';
import { ProductOptionSelectorWrapperComponent } from './product-option-selector-wrapper/product-option-selector-wrapper.component';

type ValidationError = { min?: number; max?: number; message: string };

type ProductOptionSelector = Pick<
  OptionConfiguration,
  'id' | 'name' | 'rangeType' | 'min' | 'max' | 'displayItemQuantities' | 'items'
>;

@Component({
    selector: 'pxw-product-option-selector',
    templateUrl: './product-option-selector.component.html',
    styleUrls: ['./product-option-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoDirective,
        InputRadioGroupComponent,
        InputCheckboxGroupComponent,
        PropInSelectedLanguagePipe,
        FormValidationComponent,
        FormsModule,
        ProductOptionItemRadioComponent,
        ProductOptionItemCheckboxComponent,
        ProductOptionSelectorWrapperComponent,
    ],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class ProductOptionSelectorComponent implements OnInit {
  private t = inject(TranslocoService);

  option = input.required<ProductOptionSelector>();
  stockDisplayCondition = input.required<StockDisplayCondition>();
  stockDisplayConditionValue = input.required<number>();

  selectedLanguage = input<SUPPORTED_LANGUAGES | undefined>(undefined);
  cartItemId = input<string | undefined>(undefined);
  itemType = input<'presentation' | 'option-item'>('presentation');
  displaySku = input(false);
  displayErrors = input(false);
  value = input<CartItemOptionItems | null>(null);
  displayProductPriceWithoutTaxes = input<boolean>(false);

  valueChange = output<CartItemOptionItems | undefined>();
  itemImageClick = output<ProductOptionItem>();

  selectedItemQuantities = signal<Record<string, number>>({});

  isSingleChoice = computed(() => {
    return this.option().rangeType === ProductOptionRangeType.FIXED && this.optionMin() === 1;
  });
  optionItems = computed(() => this.option().items || []);
  optionMin = computed(() => {
    if (
      this.itemType() === 'option-item' &&
      this.option().rangeType === ProductOptionRangeType.ANY
    ) {
      return 0;
    }
    if (this.itemType() === 'presentation') {
      return Math.max(1, this.option().min);
    }
    return this.option().min;
  });
  optionRequired = computed(() => this.optionMin() > 0);
  selectedItemIds = computed(() => Object.keys(this.selectedItemQuantities()));
  selectedItemsCount = computed(() =>
    Object.values(this.selectedItemQuantities()).reduce(
      (total, itemQuantity) => total + itemQuantity,
      0,
    ),
  );
  disableIncrement = computed(() => {
    switch (this.option().rangeType) {
      case ProductOptionRangeType.ANY:
        return false;
      case ProductOptionRangeType.FIXED:
        if (this.optionMin() === 1) {
          return false;
        }
        return this.optionMin() ? this.selectedItemsCount() >= this.optionMin() : false;
      case ProductOptionRangeType.RANGE:
        return this.option().max ? this.selectedItemsCount() >= this.option().max! : false;
    }
  });
  stockQtyByItemId = computed(() => {
    return this.optionItems().reduce(
      (acc, item) => {
        acc[item.id] = item.stockQty; // TODO: THIS SHOULD USE stockInfo.availableStock

        return acc;
      },
      {} as Record<string, number | null>,
    );
  });
  disabledItemIds = computed(() => {
    const disabledItemIds: string[] = [];

    // NOTE: moved out of the loop to ensure computed dependency is reevaluated on changes
    const disableIncrement = this.disableIncrement();
    const stockQtyByItemId = this.stockQtyByItemId();
    const selectedItemQuantities = this.selectedItemQuantities();
    // ------------------------------------------------------------

    this.optionItems().forEach(item => {
      if ((selectedItemQuantities[item.id] || 0) === 0 && disableIncrement) {
        disabledItemIds.push(item.id);
      }
      if (stockQtyByItemId[item.id] === 0) {
        disabledItemIds.push(item.id);
      }
    });
    return disabledItemIds;
  });
  optionInstructions = computed(() => {
    if (this.option().rangeType === ProductOptionRangeType.FIXED) {
      return this.getOptionInstruction(this.optionMin(), this.optionMin());
    }
    if (this.option().rangeType === ProductOptionRangeType.RANGE) {
      return this.getOptionInstruction(this.optionMin(), this.option().max);
    }
    return this.getOptionInstruction();
  });
  validationErrorRule = computed((): ValidationError | undefined => {
    if (this.option().rangeType === ProductOptionRangeType.FIXED) {
      return this.getOptionValidation(this.optionMin(), this.optionMin());
    }
    if (this.option().rangeType === ProductOptionRangeType.RANGE) {
      return this.getOptionValidation(this.optionMin(), this.option().max);
    }
    if (this.option().rangeType === ProductOptionRangeType.ANY) {
      return this.getOptionValidation(this.optionMin(), this.option().max);
    }
    return undefined;
  });
  selectedItems = computed(() => {
    return (
      this.optionItems()
        .map(item => ({
          ...item,
          quantity: this.selectedItemQuantities()[item.id] || 0,
        }))
        .filter(item => item.quantity > 0)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)) || []
    );
  });

  constructor() {
    effect(() => {
      const items = this.selectedItems();

      if (items.length > 0) {
        this.valueChange.emit({
          option: { ...this.option(), items: [] },
          items,
        });
      } else {
        this.valueChange.emit(undefined);
      }
    });
  }

  ngOnInit() {
    const value = this.value();

    if (value) {
      if (this.isSingleChoice()) {
        this.selectedItemQuantities.set({ [value.items[0].id]: 1 });
      } else {
        const selectedItemQuantities = value.items.reduce(
          (itemQuantities, item) => {
            if (item.quantity && item.quantity > 0) {
              itemQuantities[item.id] = item.quantity;
            }
            return itemQuantities;
          },
          {} as Record<string, number>,
        );

        this.selectedItemQuantities.set(selectedItemQuantities);
      }
    } else {
      if (this.isSingleChoice()) {
        const firstNonDisabledItem = this.optionItems().find(item => item.stockQty !== 0); // TODO: this should use stockInfo.availableStock

        if (firstNonDisabledItem) {
          this.selectedItemQuantities.set({ [firstNonDisabledItem.id]: 1 });
        }
      } else {
        this.selectedItemQuantities.set({});
      }
    }
  }

  onSingleChoiceSelected(itemId: string) {
    this.selectedItemQuantities.set({ [itemId]: 1 });
  }

  onToggleOption(optionItem: ProductOptionItem, nextState: boolean, count?: number) {
    if (nextState === false) {
      delete optionItem.quantity;

      this.selectedItemQuantities.update(itemQuantities => {
        const newItemQuantities = { ...itemQuantities };

        delete newItemQuantities[optionItem.id];

        return newItemQuantities;
      });
    } else {
      this.selectedItemQuantities.update(itemQuantities => ({
        ...itemQuantities,
        [optionItem.id]: count || 1,
      }));
    }
  }

  onOptionItemCountChange(item: ProductOptionItem, count: number) {
    if (count > 0) {
      this.onToggleOption(item, true, count);
    } else {
      this.onToggleOption(item, false);
    }
  }

  onItemImageClick(item: ProductOptionItem) {
    this.itemImageClick.emit(item);
  }

  getMaxCountForItem(item: ProductOptionItem, option: ProductOptionSelector, optionMin: number) {
    let max: number;

    if (option.rangeType === ProductOptionRangeType.RANGE && option.max) {
      max = option.max!;
    } else if (option.rangeType === ProductOptionRangeType.FIXED && optionMin) {
      max = optionMin;
    } else {
      max = Number.MAX_SAFE_INTEGER;
    }

    return Math.min(max, item.maxSelection || Number.MAX_SAFE_INTEGER);
  }

  private getOptionInstruction(min?: number, max?: number): string {
    if (!min && !max) {
      return this.t.translate('product.optionInstructionDefault');
    }
    if (min === max) {
      if (min === 1) {
        return this.t.translate('product.optionInstructionFixedOne');
      }
      return this.t.translate('product.optionInstructionFixedMany', { min });
    }
    if (min && max) {
      return this.t.translate('product.optionInstructionRange', { min, max });
    }
    if (min && !max) {
      if (min === 1) {
        return this.t.translate('product.optionInstructionRangeFromOne');
      }
      return this.t.translate('product.optionInstructionRangeFromMany', { min });
    }
    if (!min && max) {
      if (max === 1) {
        return this.t.translate('product.optionInstructionRangeToOne');
      }
      return this.t.translate('product.optionInstructionRangeToMany', { max });
    }
    return this.t.translate('product.optionInstructionDefault');
  }

  private getOptionValidation(min?: number, max?: number): ValidationError | undefined {
    if (!min && !max) {
      return undefined;
    }
    if (min === max) {
      if (min === 1) {
        return {
          message: this.getOptionInstruction(min, max),
          min,
          max,
        };
      }
      return {
        message: this.getOptionInstruction(min, max),
        min,
        max,
      };
    }
    if (min && max) {
      return {
        message: this.getOptionInstruction(min, max),
        min,
        max,
      };
    }
    if (min && !max) {
      if (min === 1) {
        return {
          message: this.getOptionInstruction(min, max),
          min,
        };
      }
      return {
        message: this.getOptionInstruction(min, max),
        min,
      };
    }
    if (!min && max) {
      if (max === 1) {
        return {
          message: this.getOptionInstruction(min, max),
          max,
        };
      }
      return {
        message: this.getOptionInstruction(min, max),
        max,
      };
    }
    return undefined;
  }
}
